import { EducationSubItem } from "@js/components/Interfaces/interfaces";
import {
    Course,
    EDUCATION_COURSES,
    EducationModule,
    STARKLINGS,
    STARKLINGS1,
} from "@js/components/EducationCourse/eduConfig";
import {
    MODULE_COMPLETE_ID,
    SOLVE_YOURSELF_ID,
} from "../educationContent/CairoMummiesContent/eduConstants";

/**
 * Get lesson and module info object from the configuration file
 * @param courseName - current course id
 * @param moduleId - current module id
 * @param lessonId - current lesson id
 * @returns object of lesson and module info
 */
export function getLessonAndModuleInfoFromConfig(
    courseName: string,
    moduleId: number,
    lessonId: number
): { lesson: EducationSubItem | null; moduleInfo: EducationSubItem | null } {
    // Create reusable null return
    const nullReturn = {
        lesson: null,
        moduleInfo: null,
    };
    // get the module, if it exists
    const module = getModuleFromConfig(courseName, moduleId);
    if (!module) {
        return nullReturn;
    }
    // For now, a lesson id of 10 = module complete
    const moduleCompleteId = 10;
    if (lessonId === moduleCompleteId) {
        return {
            lesson: {
                title: "N/A",
                description: "N/A",
                id: moduleCompleteId,
            },
            moduleInfo: module.moduleInfo,
        };
    }

    // loop through to find the lesson
    for (const currLesson of module.lessons) {
        if (currLesson.id === lessonId) {
            return { lesson: currLesson, moduleInfo: module.moduleInfo };
        }
    }

    return nullReturn;
}

/**
 * Get the course from the configuration file
 * @param courseName - current course id
 * @returns the course or null
 */
export function getCourseFromConfig(courseName: string): Course | null {
    for (const currCourse of EDUCATION_COURSES) {
        if (currCourse.courseName === courseName) {
            return currCourse;
        }
    }
    return null;
}

/**
 * Gets the module from the config file
 * @param courseName - current course name
 * @param moduleId - current module id
 * @returns the module or null
 */
export function getModuleFromConfig(
    courseName: string,
    moduleId: number
): EducationModule | null {
    const course = getCourseFromConfig(courseName);
    if (!course) {
        return null;
    }

    for (const currModGroup of course.moduleGroup) {
        for (const currModule of currModGroup.modules) {
            if (currModule.moduleInfo.id === moduleId) {
                return currModule;
            }
        }
    }
    return null;
}

/**
 * Returns the badge token id associated with the given course and module
 * @param courseName - course id
 * @param moduleId - module id
 * @returns the reward badge token id for the course + module
 */
export function getBadgeTokenId(courseName: string, moduleId: number): number {
    const module = getModuleFromConfig(courseName, moduleId);
    if (!module) {
        return -1;
    }

    const badgeTokenId = module.moduleInfo.moduleComplete.badgeTokenId;
    if (!badgeTokenId || badgeTokenId < 1) {
        return -1;
    }

    return badgeTokenId;
}

/**
 * Helper function to check if we're on the last module of a course
 * @param {string }courseName  - course id we're on
 * @param {number} moduleId  - module id we're on
 * @returns boolean - true if we are on the last module of the course
 */
export function isLastModuleOfCourse(courseName: string, moduleId: number) {
    const course = getCourseFromConfig(courseName);
    if (!course) return false;
    const lastModGroup = course.moduleGroup.slice(-1)[0];
    const lastModule = lastModGroup.modules.slice(-1)[0];
    const lastModuleIdIndex = lastModule.moduleInfo.id;
    return moduleId === lastModuleIdIndex;
}

/**
 * Get the lesson count from a module
 * @param courseName - current course id
 * @param moduleId - current module id
 * @returns # of lessons in module
 */
export function getLessonCountFromModule(
    courseName: string,
    moduleId: number
): number {
    const module = getModuleFromConfig(courseName, moduleId);
    if (!module) {
        return 0;
    }

    const solveYourselfLesson = module.lessons.find(
        (lesson: any) => lesson.id === SOLVE_YOURSELF_ID
    );
    if (!solveYourselfLesson) {
        return module.lessons.length;
    }
    return module.lessons.length - 1;
}

/**
 * Checks if a user has modified code that should not be touched
 * @param userCode - userCode in editor
 * @param courseName - current course id
 * @param moduleId - current module id
 * @param lessonId - current lesson id
 * @returns blank string if default code has been modified, returns an error message of what default code has been modified otherwise
 */
export function hasUserModifiedDefaultCode(
    userCode: string,
    courseName: string,
    moduleId: number,
    lessonId: number
): string {
    const { lesson } = getLessonAndModuleInfoFromConfig(
        courseName,
        moduleId,
        lessonId
    );
    if (!lesson) {
        return "No lesson";
    }
    const defaultStartCode = lesson.defaultStartCode;
    const defaultEndCode = lesson.defaultEndCode;
    if (
        defaultStartCode &&
        userCode.substring(0, defaultStartCode.length) !== defaultStartCode
    ) {
        return "Default start code has been modified";
    }

    if (
        defaultEndCode &&
        userCode.substring(
            userCode.length - defaultEndCode.length,
            userCode.length
        ) !== defaultEndCode
    ) {
        return "Default end code has been modified";
    }
    return "";
}

/**
 * Redirect to the correct lesson
 * @param {string} courseName The course id to redirect to
 * @param {number} moduleId The module number of the course to redirect to
 * @param {number} lessonId The lesson number of the module to redirect to
 */
export function getLessonUrl(
    courseName: string,
    moduleId: number,
    lessonId: number
) {
    if (lessonId === MODULE_COMPLETE_ID) {
        return `/education/course/${courseName}/module/${moduleId}/complete`;
    }
    return `/education/course/${courseName}/module/${moduleId}/lesson/${lessonId}`;
}

/**
 * Redirect to the correct module
 * @param {string} courseName The course id to redirect to
 * @param {number} moduleId The module number of the course to redirect to
 */
export function getModuleUrl(courseName: string, moduleId: number) {
    return `/education/course/${courseName}/module/${moduleId}`;
}

/**
 * URL for the course complete page
 * @param {string} courseName The course id to redirect to
 */
export function getCourseCompleteUrl(courseName: string) {
    return `/education/course/${courseName}/complete`;
}

/**
 * Check if user should have access to a particular lesson id
 * @param allCourseProgress - all course progress for the user
 * @param courseName - course id
 * @param moduleId - module id
 * @param lessonId - lesson id
 * @param numLessons - numnber of lessons in the module
 * @returns true if user has access
 */
export function hasAccessToLesson(
    allCourseProgress: any,
    courseName: string,
    moduleId: number,
    lessonId: number,
    numLessons: number
) {
    //Always give access to the first lesson and a solve it yourself problem
    if (lessonId === 0 || lessonId === SOLVE_YOURSELF_ID) {
        return true;
    }

    //Always give access for starklings, unless we are checking the access for the "lesson" after the final lesson
    //In this cass the user needs to have finished all the lessoms
    if (isStarklings(courseName) && lessonId !== numLessons) {
        return true;
    }

    // user has no progress at all
    if (!allCourseProgress || allCourseProgress.length === 0) {
        return false;
    }

    const lessonsCompleted = getLessonsCompletedInModule(
        allCourseProgress,
        courseName,
        moduleId
    );

    // User has no progress on this module
    if (!lessonsCompleted || lessonsCompleted.length === 0) {
        return false;
    }

    // if they've solved the entire lesson themselves, unlock access to all lessons in that module
    if (lessonsCompleted.includes(SOLVE_YOURSELF_ID)) {
        return true;
    }

    // make sure all previous lessons (up to lessonId) have been completed
    for (let i = 0; i < lessonId; i += 1) {
        if (!lessonsCompleted.includes(i)) {
            return false;
        }
    }

    return true;
}

/**
 * Check if user has completed the given lesson
 * @param allCourseProgress - all course progress for the user
 * @param courseName - course name
 * @param moduleId - module id
 * @param lessonId - lesson id
 * @returns true if user has completed the lesson
 */
export function isLessonComplete(
    allCourseProgress: any,
    courseName: string,
    moduleId: number,
    lessonId: number
): boolean {
    const lessonsCompleted = getLessonsCompletedInModule(
        allCourseProgress,
        courseName,
        moduleId
    );
    return lessonsCompleted.includes(lessonId);
}

/**
 * Tells us if the course is starklings
 * @param courseName - course id
 * @returns - true if the course is starklings
 */
export function isStarklings(courseName: string) {
    return courseName === STARKLINGS || courseName === STARKLINGS1;
}

/**
 * Get the percentage complete a user is of a module
 */
export function getPercentageComplete(
    allCourseProgress: any,
    courseName: string,
    moduleId: number,
    numLessons: number
): number {
    const lessonsCompleted = getLessonsCompletedInModule(
        allCourseProgress,
        courseName,
        moduleId
    );

    // User has no progress on this module
    if (!lessonsCompleted || lessonsCompleted.length === 0) {
        return 0;
    }

    if (lessonsCompleted.includes(SOLVE_YOURSELF_ID)) {
        return 100;
    }

    return Math.round((lessonsCompleted.length / numLessons) * 100);
}

function getLessonsCompletedInModule(
    allCourseProgress: any,
    courseName: string,
    moduleId: number
): number[] {
    if (!allCourseProgress || allCourseProgress.length === 0) {
        return [];
    }

    return allCourseProgress
        .filter((lp: any) => {
            return (
                lp.course.courseName === courseName &&
                lp.course.moduleId === moduleId &&
                lp.isComplete
            );
        })
        .map((lp: any) => lp.course.lessonId);
}

export function getLessonProgress(
    allCourseProgress: any,
    courseName: string,
    moduleId: number,
    lessonId: number
) {
    const lessonProgressArr = allCourseProgress.filter((lp: any) => {
        return (
            lp.course.courseName === courseName &&
            lp.course.moduleId === moduleId &&
            lp.course.lessonId === lessonId
        );
    });
    if (lessonProgressArr.length === 1) {
        return lessonProgressArr[0];
    }

    return null;
}

/**
 * If the module has all the lessons completed return true, false otherwise
 * @param allCourseProgress - all course progress for the user
 * @param courseName - course id
 * @param moduleId - module id
 * @param numLessons - number of lessons in a module
 * @returns whether user has completed the module
 */
export function isModuleComplete(
    allCourseProgress: any,
    courseName: string,
    moduleId: number,
    numLessons: number
) {
    return (
        getPercentageComplete(
            allCourseProgress,
            courseName,
            moduleId,
            numLessons
        ) == 100
    );
}
